import * as React from "react"
// Type-only, so the bank module is not pulled into the grading bundle. Bloom's
// levels are tagged once for the whole app; a second union here would be free
// to drift from the one the question bank filters on.
import type { BloomLevel } from "@/lib/mock/assessment-add-questions"
import {
  MOCK_STUDENTS,
  resolveAssessmentLifecycleStatus,
} from "@/lib/mock/exam-data"
import {
  GRADING_KPI_LABEL,
  SCORING_KPI_LABEL,
  gradingKpiDescription,
} from "@/lib/grading/copy"

export type QuestionType =
  | "single-select"
  | "true-false"
  | "hotspot"
  | "numeric"
  | "multi-select"
  | "fill-blank"
  | "matching"
  | "essay"
  | "short-answer"
  | "text-response"

export type ScoringMode = "full" | "partial" | "partial-negative"

/**
 * Topic shown in the item-analysis panel.
 *
 * Read off `sectionName` rather than stored twice: sections are already named
 * `Section A · Biochemistry`, and a separate `topic` field would be free to
 * drift from the section a question actually sits in.
 */
export function questionTopic(question: GradingQuestion): string {
  const [, topic] = question.sectionName.split("·")
  return (topic ?? question.sectionName).trim()
}
export type ScoreStatus =
  | "Ungraded"
  | "Partially Evaluated"
  | "Graded"
  | "Scores Released"

/** True when the submission still owes scoring work (none started, or in progress). */
export function needsScoringStatus(status: ScoreStatus): boolean {
  return status === "Ungraded" || status === "Partially Evaluated"
}

/** True only when no question on the submission has points yet. */
export function isEvaluationNotStarted(status: ScoreStatus): boolean {
  return status === "Ungraded"
}

/** True when every active question has points (ready for release cohort stats). */
export function isFullyScored(status: ScoreStatus): boolean {
  return status === "Graded" || status === "Scores Released"
}

/**
 * Under blind grading, student names stay masked until every active submission
 * is fully scored. When blind grading is off, names are always shown.
 */
export function isSubmissionIdentityRevealed(
  state: AssessmentGradingState,
): boolean {
  if (!state.blindGrading) return true
  const active = getActiveSubmissions(state)
  return active.length > 0 && active.every((s) => isFullyScored(s.scoreStatus))
}

export function submissionDisplayName(
  state: AssessmentGradingState,
  submission: GradingSubmission,
): string {
  return isSubmissionIdentityRevealed(state)
    ? submission.studentName
    : submission.maskLabel
}

/** Roster rows show student avatars for every row or for none (never mixed). */
export function submissionShowsStudentIdentity(
  state: AssessmentGradingState,
  _submission: GradingSubmission,
): boolean {
  return isSubmissionIdentityRevealed(state)
}

export function submissionRosterLabel(
  state: AssessmentGradingState,
  submission: GradingSubmission,
): string {
  return submissionShowsStudentIdentity(state, submission)
    ? submission.studentName
    : submission.maskLabel
}

export function getActiveSubmissions(
  state: AssessmentGradingState,
): GradingSubmission[] {
  return state.submissions.filter((s) => !s.thrownOut)
}

/** Latest release stamp on any active submission (`YYYY-MM-DD` or fallback). */
export function assessmentResultsPublishedOn(
  state: AssessmentGradingState,
  fallbackYmd?: string,
): string | null {
  const stamps = getActiveSubmissions(state)
    .map((submission) => submission.scoresReleasedAt?.trim())
    .filter((value): value is string => Boolean(value))

  const ymdValues = stamps
    .map((raw) => {
      const match = raw.match(/^(\d{4}-\d{2}-\d{2})/)
      return match?.[1] ?? null
    })
    .filter((value): value is string => Boolean(value))

  if (ymdValues.length > 0) {
    return ymdValues.sort().at(-1) ?? null
  }

  if (stamps.length > 0 && fallbackYmd) return fallbackYmd
  return fallbackYmd ?? null
}

/** Peers with every question scored — excludes partial and unscored rows. */
export function getFullyScoredSubmissions(
  state: AssessmentGradingState,
  options?: { excludeId?: string },
): GradingSubmission[] {
  const excludeId = options?.excludeId
  return getActiveSubmissions(state).filter(
    (s) => isFullyScored(s.scoreStatus) && s.id !== excludeId,
  )
}

export function cohortClassAverage(
  state: AssessmentGradingState,
  submissions: GradingSubmission[],
): number | null {
  if (submissions.length === 0) return null
  const sum = submissions.reduce(
    (acc, s) => acc + submissionTotals(state, s).earned,
    0,
  )
  return Math.round(sum / submissions.length)
}

/** Questions with points vs active questions — drives the partial completion ring. */
export function submissionQuestionCompletion(
  state: AssessmentGradingState,
  submission: GradingSubmission,
): { scoredCount: number; totalCount: number } {
  const activeQuestions = state.questions.filter((q) => !q.thrownOut)
  const totalCount = activeQuestions.length
  const scoredCount = activeQuestions.filter((q) => {
    const response = submission.responses.find((r) => r.questionId === q.id)
    return response != null && responsePointsEarned(q, response) !== null
  }).length
  return { scoredCount, totalCount }
}
export type GradeStatus = "Not Set" | "Set" | "Grades Released"

export const SUBJECTIVE_TYPES: QuestionType[] = [
  "essay",
  "short-answer",
  "text-response",
]
export const PARTIAL_ELIGIBLE_TYPES: QuestionType[] = [
  "multi-select",
  "fill-blank",
  "matching",
]

export function isSubjectiveType(type: QuestionType) {
  return SUBJECTIVE_TYPES.includes(type)
}
export function isPartialEligibleType(type: QuestionType) {
  return PARTIAL_ELIGIBLE_TYPES.includes(type)
}

export interface GradingQuestion {
  id: string
  sectionName: string
  stem: string
  type: QuestionType
  basePoints: number
  adjustedPoints?: number
  options?: { key: string; text: string }[]
  correctOptionKeys?: string[]
  acceptableRanges?: { min: number; max: number }[]
  blanks?: { acceptableKeywords: string[] }[]
  matchingPairs?: { id: string; left: string; right: string }[]
  negativeMarking?: { enabled: boolean; penalty: number }
  /** Cognitive level the item is tagged with, for the item-analysis panel. */
  bloom?: BloomLevel
  scoringMode?: ScoringMode
  bonusForAll?: boolean
  thrownOut?: boolean
  throwOutComment?: string
}

export function effectivePoints(q: GradingQuestion): number {
  return q.adjustedPoints ?? q.basePoints
}

export interface GradedResponse {
  questionId: string
  selectedOptionKeys?: string[]
  numericValue?: number
  blanksFilled?: string[]
  matchingSelections?: Record<string, string>
  textResponse?: string
  pointsAwarded: number | null
  overridden?: boolean
}

export interface GradingSubmission {
  id: string
  assessmentId: string
  studentId: string
  studentName: string
  studentEmail: string
  maskLabel: string
  submittedAt: string
  responses: GradedResponse[]
  scoreStatus: ScoreStatus
  gradeStatus: GradeStatus
  grade: string
  thrownOut: boolean
  /** Per-student shuffled question ids (matches delivery randomization). */
  questionOrder: string[]
  /** Faculty who completed manual scoring, when applicable. */
  scoredByName?: string
  scoredByEmail?: string
  scoresReleasedAt?: string
  gradesReleasedAt?: string
  lastReleasedTotal?: number
  lastNotifiedAt?: string
}

export interface AssessmentGradingState {
  assessmentId: string
  blindGrading: boolean
  questions: GradingQuestion[]
  submissions: GradingSubmission[]
  displayOrder: string[]
  nextQueue: string[]
  regradeInProgress: boolean
  regradeProgress: { done: number; total: number } | null
  lastRegradeSummary: { notifiedCount: number } | null
  /** False until faculty opens the submissions queue (Lab Practical demo). */
  scoringStarted?: boolean
}

const STORE: Record<string, AssessmentGradingState> = {}
const LISTENERS: Record<string, Set<() => void>> = {}

function notify(assessmentId: string) {
  LISTENERS[assessmentId]?.forEach((cb) => cb())
}

export function subscribeGradingState(assessmentId: string, cb: () => void) {
  if (!LISTENERS[assessmentId]) LISTENERS[assessmentId] = new Set()
  LISTENERS[assessmentId].add(cb)
  return () => {
    LISTENERS[assessmentId]?.delete(cb)
  }
}

/* ── Deterministic PRNG (seeded by assessment/submission id, same pattern as submission-details.tsx) ── */
function seededRandom(seed: string) {
  let hash = 0
  for (let i = 0; i < seed.length; i++)
    hash = seed.charCodeAt(i) + ((hash << 5) - hash)
  return () => {
    const x = Math.sin(hash++) * 10000
    return x - Math.floor(x)
  }
}

function shuffle<T>(arr: T[], rand: () => number): T[] {
  const out = [...arr]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/**
 * Cognitive level per item, applied to the bank below.
 *
 * Kept as a map rather than a field on each literal so the bank stays readable
 * as a list of questions and their keys, which is what most of this file's
 * readers come here for.
 */
const QUESTION_BLOOM: Record<string, BloomLevel> = {
  q1: "Remember",
  q2: "Remember",
  q4: "Analyze",
  q5: "Understand",
  q6: "Understand",
  q7: "Remember",
  q8: "Understand",
  q9: "Apply",
  q10: "Analyze",
  q11: "Evaluate",
  q12: "Understand",
}

/* ── Canonical question bank (reused per assessment, mock-only) ─────────── */
function buildQuestions(): GradingQuestion[] {
  const bank: GradingQuestion[] = [
    {
      id: "q1",
      sectionName: "Section A · Biochemistry",
      type: "single-select",
      basePoints: 5,
      stem: "Citric acid cycle takes place in:",
      options: [
        { key: "A", text: "Cytoplasm" },
        { key: "B", text: "Mitochondria" },
        { key: "C", text: "Endoplasmic reticulum" },
        { key: "D", text: "Ribosomes" },
      ],
      correctOptionKeys: ["B"],
    },
    {
      id: "q2",
      sectionName: "Section A · Biochemistry",
      type: "single-select",
      basePoints: 5,
      stem: "Enzyme involved in breakdown of triglycerides to free fatty acids.",
      options: [
        { key: "A", text: "Amylase" },
        { key: "B", text: "Protease" },
        { key: "C", text: "Lipase" },
        { key: "D", text: "Cellulase" },
      ],
      correctOptionKeys: ["C"],
      negativeMarking: { enabled: true, penalty: 2 },
    },
    {
      id: "q4",
      sectionName: "Section A · Biochemistry",
      type: "multi-select",
      basePoints: 6,
      stem: "Select all receptors activated by epinephrine during anaphylaxis response.",
      options: [
        { key: "A", text: "Alpha-1 adrenergic" },
        { key: "B", text: "Beta-2 adrenergic" },
        { key: "C", text: "H1 histamine" },
        { key: "D", text: "Muscarinic acetylcholine" },
      ],
      correctOptionKeys: ["A", "B"],
      scoringMode: "partial-negative",
    },
    {
      id: "q5",
      sectionName: "Section B · Nutrition & Enzymology",
      type: "multi-select",
      basePoints: 4,
      stem: "Select all lipid-soluble vitamins.",
      options: [
        { key: "A", text: "Vitamin A" },
        { key: "B", text: "Vitamin C" },
        { key: "C", text: "Vitamin D" },
        { key: "D", text: "Vitamin B12" },
      ],
      correctOptionKeys: ["A", "C"],
      scoringMode: "partial",
    },
    {
      id: "q6",
      sectionName: "Section B · Nutrition & Enzymology",
      type: "multi-select",
      basePoints: 4,
      stem: "Select all enzymes involved in glycolysis.",
      options: [
        { key: "A", text: "Hexokinase" },
        { key: "B", text: "Pyruvate kinase" },
        { key: "C", text: "Citrate synthase" },
        { key: "D", text: "Catalase" },
      ],
      correctOptionKeys: ["A", "B"],
      scoringMode: "full",
    },
    {
      id: "q7",
      sectionName: "Section B · Nutrition & Enzymology",
      type: "fill-blank",
      basePoints: 4,
      stem: "The _____ nerve provides motor innervation to the muscles of facial expression, while the _____ nerve provides sensory innervation to the anterior 2/3 of the tongue.",
      blanks: [
        { acceptableKeywords: ["facial", "cn vii", "seventh", "7th"] },
        { acceptableKeywords: ["trigeminal", "cn v", "fifth", "5th"] },
      ],
      scoringMode: "partial",
    },
    {
      id: "q8",
      sectionName: "Section B · Nutrition & Enzymology",
      type: "matching",
      basePoints: 8,
      stem: "Match the deficiency disease to its causative vitamin/mineral.",
      matchingPairs: [
        { id: "p1", left: "Scurvy", right: "Vitamin C" },
        { id: "p2", left: "Rickets", right: "Vitamin D" },
        { id: "p3", left: "Goiter", right: "Iodine" },
        { id: "p4", left: "Beriberi", right: "Vitamin B1" },
      ],
      scoringMode: "partial-negative",
    },
    {
      id: "q9",
      sectionName: "Section C · Chemistry & Physiology",
      type: "single-select",
      basePoints: 5,
      stem: "Which of the following structures LACK hydrogen bonding?",
      options: [
        { key: "A", text: "Water" },
        { key: "B", text: "Alkanes" },
        { key: "C", text: "Ammonia" },
        { key: "D", text: "Ethanol" },
      ],
      correctOptionKeys: ["B"],
    },
    {
      id: "q10",
      sectionName: "Section C · Chemistry & Physiology",
      type: "single-select",
      basePoints: 5,
      stem: "All of the following are true for enzymes, EXCEPT:",
      options: [
        {
          key: "A",
          text: "They increase the activation energy of the reaction",
        },
        { key: "B", text: "They act as biological catalysts" },
        { key: "C", text: "They are highly specific for their substrates" },
        { key: "D", text: "Their activity is pH and temperature dependent" },
      ],
      correctOptionKeys: ["A"],
    },
    {
      id: "q11",
      sectionName: "Section D · Written Response",
      type: "essay",
      basePoints: 15,
      stem: "Discuss the pathophysiology of heart failure with preserved ejection fraction (HFpEF).",
    },
    {
      id: "q12",
      sectionName: "Section D · Written Response",
      type: "short-answer",
      basePoints: 8,
      stem: "Name the primary neurotransmitter released at the neuromuscular junction and its receptor type.",
    },
  ]

  return bank.map((question) => ({
    ...question,
    bloom: QUESTION_BLOOM[question.id],
  }))
}

/* ── Objective auto-grading ──────────────────────────────────────────────── */
function matchesAnyKeyword(value: string | undefined, keywords: string[]) {
  if (!value) return false
  const v = value.trim().toLowerCase()
  return keywords.some((k) => v.includes(k.toLowerCase()))
}

/** Per-blank correctness, for display in the submission grading view (fill-blank has no single "correct option" the way single/multi-select do). */
export function blankIsCorrect(
  question: GradingQuestion,
  response: GradedResponse,
  index: number
): boolean {
  const blank = question.blanks?.[index]
  if (!blank) return false
  return matchesAnyKeyword(
    response.blanksFilled?.[index],
    blank.acceptableKeywords
  )
}

function scorePartial(
  question: GradingQuestion,
  response: GradedResponse,
  pts: number
): number {
  const mode = question.scoringMode || "full"
  if (question.type === "multi-select") {
    const correct = new Set(question.correctOptionKeys || [])
    const chosen = response.selectedOptionKeys || []
    if (mode === "full") {
      const exact =
        chosen.length === correct.size && chosen.every((k) => correct.has(k))
      return exact ? pts : 0
    }
    const perUnit = pts / correct.size
    const correctCount = chosen.filter((k) => correct.has(k)).length
    const incorrectCount = chosen.filter((k) => !correct.has(k)).length
    let score = correctCount * perUnit
    if (mode === "partial-negative") score -= incorrectCount * perUnit
    return Math.max(0, score)
  }
  if (question.type === "fill-blank") {
    const blanks = question.blanks || []
    const perUnit = pts / blanks.length
    let correctCount = 0
    let incorrectCount = 0
    blanks.forEach((b, i) => {
      const val = response.blanksFilled?.[i]
      if (matchesAnyKeyword(val, b.acceptableKeywords)) correctCount++
      else if (val && val.trim()) incorrectCount++
    })
    if (mode === "full") return correctCount === blanks.length ? pts : 0
    let score = correctCount * perUnit
    if (mode === "partial-negative") score -= incorrectCount * perUnit
    return Math.max(0, score)
  }
  if (question.type === "matching") {
    const pairs = question.matchingPairs || []
    const perUnit = pts / pairs.length
    let correctCount = 0
    let incorrectCount = 0
    pairs.forEach((p) => {
      const chosen = response.matchingSelections?.[p.id]
      if (chosen === p.right) correctCount++
      else if (chosen) incorrectCount++
    })
    if (mode === "full") return correctCount === pairs.length ? pts : 0
    let score = correctCount * perUnit
    if (mode === "partial-negative") score -= incorrectCount * perUnit
    return Math.max(0, score)
  }
  return 0
}

/** Auto-grades one response against the current question definition. Subjective types are untouched — caller preserves the manually-entered value (or null if not yet scored). */
export function scoreResponse(
  question: GradingQuestion,
  response: GradedResponse
): number | null {
  if (isSubjectiveType(question.type)) return response.pointsAwarded
  if (question.thrownOut) return 0
  const pts = effectivePoints(question)
  if (question.bonusForAll) return pts

  if (
    question.type === "single-select" ||
    question.type === "true-false" ||
    question.type === "hotspot"
  ) {
    const key = response.selectedOptionKeys?.[0]
    const correct =
      key != null && (question.correctOptionKeys || []).includes(key)
    if (correct) return pts
    const penalty = question.negativeMarking?.enabled
      ? question.negativeMarking.penalty
      : 0
    return Math.max(0, 0 - penalty)
  }
  if (question.type === "numeric") {
    const val = response.numericValue
    const inRange =
      val != null &&
      (question.acceptableRanges || []).some(
        (r) => val >= r.min && val <= r.max
      )
    if (inRange) return pts
    const penalty = question.negativeMarking?.enabled
      ? question.negativeMarking.penalty
      : 0
    return Math.max(0, 0 - penalty)
  }
  if (isPartialEligibleType(question.type))
    return scorePartial(question, response, pts)
  return 0
}

/* ── Mock submission generation ──────────────────────────────────────────── */
interface SubmissionPlan {
  scoreStatus: ScoreStatus
  gradeStatus: GradeStatus
  grade: string
  thrownOut: boolean
  fillSubjective: boolean
}

const MOCK_GRADING_FACULTY = [
  { name: "Dr. Sarah Jenkins", email: "s.jenkins@example.edu" },
  { name: "Dr. Marie Curie", email: "m.curie@example.edu" },
] as const

const SUBMISSION_PLANS: SubmissionPlan[] = [
  {
    scoreStatus: "Scores Released",
    gradeStatus: "Grades Released",
    grade: "A-",
    thrownOut: false,
    fillSubjective: true,
  },
  {
    scoreStatus: "Scores Released",
    gradeStatus: "Set",
    grade: "B+",
    thrownOut: false,
    fillSubjective: true,
  },
  {
    scoreStatus: "Scores Released",
    gradeStatus: "Not Set",
    grade: "",
    thrownOut: false,
    fillSubjective: true,
  },
  {
    scoreStatus: "Graded",
    gradeStatus: "Not Set",
    grade: "",
    thrownOut: false,
    fillSubjective: true,
  },
  {
    scoreStatus: "Graded",
    gradeStatus: "Not Set",
    grade: "",
    thrownOut: false,
    fillSubjective: true,
  },
  {
    scoreStatus: "Ungraded",
    gradeStatus: "Not Set",
    grade: "",
    thrownOut: false,
    fillSubjective: false,
  },
  {
    scoreStatus: "Ungraded",
    gradeStatus: "Not Set",
    grade: "",
    thrownOut: false,
    fillSubjective: false,
  },
  {
    scoreStatus: "Ungraded",
    gradeStatus: "Not Set",
    grade: "",
    thrownOut: false,
    fillSubjective: false,
  },
  {
    scoreStatus: "Ungraded",
    gradeStatus: "Not Set",
    grade: "",
    thrownOut: true,
    fillSubjective: false,
  },
]

const SUBJECTIVE_SAMPLE_RESPONSES: Record<string, string> = {
  q11: "HFpEF results from impaired ventricular relaxation and increased myocardial stiffness, leading to elevated left ventricular filling pressures despite a preserved ejection fraction (>=50%). The underlying mechanism is diastolic dysfunction: the ventricle fails to relax and fill normally, so pressure builds up and backs into the left atrium and pulmonary circulation, producing symptoms of congestion (dyspnea, edema) even though systolic pumping function is intact. Common contributors include chronic hypertension (causing concentric hypertrophy and a stiffer chamber), aging myocardium (reduced compliance and impaired active relaxation), diabetes (myocardial fibrosis and microvascular dysfunction), and obesity. Diagnosis relies on a combination of clinical signs of heart failure, preserved EF on echocardiogram, and evidence of diastolic dysfunction or elevated filling pressures (e.g., elevated E/e' ratio, left atrial enlargement, elevated BNP). Management focuses on controlling blood pressure, managing volume status with diuretics, and treating comorbidities, since there are fewer proven mortality-reducing therapies for HFpEF compared to HFrEF.",
  q12: "Acetylcholine is released at the neuromuscular junction and binds nicotinic acetylcholine receptors on the motor end plate, triggering depolarization and muscle contraction.",
}

function buildSubmissions(
  assessmentId: string,
  questions: GradingQuestion[]
): GradingSubmission[] {
  const rand = seededRandom(assessmentId)
  const rosterOffset = Math.floor(rand() * MOCK_STUDENTS.length)
  const roster = Array.from(
    { length: SUBMISSION_PLANS.length },
    (_, i) => MOCK_STUDENTS[(rosterOffset + i) % MOCK_STUDENTS.length]
  )

  return SUBMISSION_PLANS.map((plan, i) => {
    const student = roster[i]
    const subRand = seededRandom(`${assessmentId}-${student.studentId}-${i}`)
    const aptitude = 0.55 + subRand() * 0.4 // per-submission chance of answering correctly

    const responses: GradedResponse[] = questions.map((q) => {
      if (isSubjectiveType(q.type)) {
        const textResponse =
          SUBJECTIVE_SAMPLE_RESPONSES[q.id] ||
          "Student response submitted for this question."
        if (!plan.fillSubjective)
          return { questionId: q.id, textResponse, pointsAwarded: null }
        const pts = effectivePoints(q)
        const awarded = Math.round(pts * (0.55 + subRand() * 0.4) * 2) / 2
        return { questionId: q.id, textResponse, pointsAwarded: awarded }
      }

      let response: GradedResponse
      if (
        q.type === "single-select" ||
        q.type === "true-false" ||
        q.type === "hotspot"
      ) {
        const correct = subRand() < aptitude
        const options = q.options || []
        const key = correct
          ? (q.correctOptionKeys?.[0] ?? options[0]?.key)
          : (options.find((o) => !q.correctOptionKeys?.includes(o.key))?.key ??
            options[0]?.key)
        response = {
          questionId: q.id,
          selectedOptionKeys: key ? [key] : [],
          pointsAwarded: null,
        }
      } else if (q.type === "numeric") {
        const range = q.acceptableRanges?.[0] ?? { min: 0, max: 1 }
        const correct = subRand() < aptitude
        const val = correct
          ? (range.min + range.max) / 2
          : range.max + 0.3 + subRand()
        response = {
          questionId: q.id,
          numericValue: Number(val.toFixed(2)),
          pointsAwarded: null,
        }
      } else if (q.type === "multi-select") {
        const correctKeys = q.correctOptionKeys || []
        const allKeys = (q.options || []).map((o) => o.key)
        const chosen = allKeys.filter((k) => {
          const shouldPick = correctKeys.includes(k)
            ? subRand() < aptitude
            : subRand() > aptitude
          return shouldPick
        })
        response = {
          questionId: q.id,
          selectedOptionKeys: chosen,
          pointsAwarded: null,
        }
      } else if (q.type === "fill-blank") {
        const blanksFilled = (q.blanks || []).map((b) =>
          subRand() < aptitude ? b.acceptableKeywords[0] : "unsure"
        )
        response = { questionId: q.id, blanksFilled, pointsAwarded: null }
      } else if (q.type === "matching") {
        const matchingSelections: Record<string, string> = {}
        const rights = (q.matchingPairs || []).map((p) => p.right)
        ;(q.matchingPairs || []).forEach((p) => {
          matchingSelections[p.id] =
            subRand() < aptitude
              ? p.right
              : rights[Math.floor(subRand() * rights.length)]
        })
        response = { questionId: q.id, matchingSelections, pointsAwarded: null }
      } else {
        response = { questionId: q.id, pointsAwarded: null }
      }
      response.pointsAwarded = scoreResponse(q, response)
      return response
    })

    const activeQuestionIds = questions
      .filter((q) => !q.thrownOut)
      .map((q) => q.id)
    const questionOrder = shuffle(activeQuestionIds, subRand)

    const totals = computeTotals(questions, responses)
    const faculty = MOCK_GRADING_FACULTY[i % MOCK_GRADING_FACULTY.length]
    const hasManualScoring =
      plan.fillSubjective ||
      plan.scoreStatus === "Graded" ||
      plan.scoreStatus === "Scores Released"
    const submission: GradingSubmission = {
      id: `${assessmentId}-sub-${i + 1}`,
      assessmentId,
      studentId: student.studentId,
      studentName: student.name,
      studentEmail: student.email,
      maskLabel: `Submission #${i + 1}`,
      submittedAt: `2026-07-${String(20 + (i % 5)).padStart(2, "0")} ${String(9 + i).padStart(2, "0")}:${String((i * 7) % 60).padStart(2, "0")}`,
      responses,
      questionOrder,
      scoreStatus: plan.scoreStatus,
      gradeStatus: plan.gradeStatus,
      grade: plan.grade,
      thrownOut: plan.thrownOut,
      scoredByName: hasManualScoring ? faculty.name : undefined,
      scoredByEmail: hasManualScoring ? faculty.email : undefined,
      scoresReleasedAt:
        plan.scoreStatus === "Scores Released" ? "2026-07-25 10:00" : undefined,
      gradesReleasedAt:
        plan.gradeStatus === "Grades Released" ? "2026-07-26 09:00" : undefined,
      lastReleasedTotal:
        plan.scoreStatus === "Scores Released" ? totals.earned : undefined,
    }
    return submission
  })
}

/** Auto-grades objective responses in place; subjective slots stay as-is. */
function autoGradeObjectiveResponses(
  questions: GradingQuestion[],
  submission: GradingSubmission,
) {
  for (const q of questions) {
    if (q.thrownOut || isSubjectiveType(q.type)) continue
    const response = submission.responses.find((r) => r.questionId === q.id)
    if (!response) continue
    response.pointsAwarded = scoreResponse(q, response)
  }
}

/**
 * Points shown in UI and KPI math. Objective items always resolve via the key
 * even when faculty scoring has not started and stored points are null.
 */
export function responsePointsEarned(
  question: GradingQuestion,
  response: GradedResponse,
): number | null {
  if (response.pointsAwarded !== null) return response.pointsAwarded
  if (question.thrownOut || isSubjectiveType(question.type)) return null
  return scoreResponse(question, response)
}

export function computeTotals(
  questions: GradingQuestion[],
  responses: GradedResponse[]
) {
  const active = questions.filter((q) => !q.thrownOut)
  const possible = active.reduce((s, q) => s + effectivePoints(q), 0)
  const earned = active.reduce((s, q) => {
    const r = responses.find((rr) => rr.questionId === q.id)
    if (!r) return s
    return s + (responsePointsEarned(q, r) ?? 0)
  }, 0)
  const percent = possible > 0 ? Math.round((earned / possible) * 100) : 0
  return { earned, possible, percent }
}

export function submissionTotals(
  state: AssessmentGradingState,
  submission: GradingSubmission
) {
  return computeTotals(state.questions, submission.responses)
}

/** Score scoped to only the auto-graded (objective) questions — always computable, even before any subjective question has been scored. */
export function objectiveTotals(
  questions: GradingQuestion[],
  responses: GradedResponse[]
) {
  const active = questions.filter(
    (q) => !q.thrownOut && !isSubjectiveType(q.type)
  )
  const possible = active.reduce((s, q) => s + effectivePoints(q), 0)
  const earned = active.reduce((s, q) => {
    const r = responses.find((rr) => rr.questionId === q.id)
    if (!r) return s
    return s + (responsePointsEarned(q, r) ?? 0)
  }, 0)
  return { earned, possible }
}

/** Score scoped to subjective (essay / short-answer) questions awaiting or after manual scoring. */
export function subjectiveTotals(
  questions: GradingQuestion[],
  responses: GradedResponse[]
) {
  const active = questions.filter(
    (q) => !q.thrownOut && isSubjectiveType(q.type)
  )
  const possible = active.reduce((s, q) => s + effectivePoints(q), 0)
  const earned = active.reduce((s, q) => {
    const r = responses.find((rr) => rr.questionId === q.id)
    return s + (r?.pointsAwarded ?? 0)
  }, 0)
  return { earned, possible }
}

function responseHasScore(
  question: GradingQuestion,
  response: GradedResponse | undefined,
): boolean {
  if (!response) return false
  return responsePointsEarned(question, response) !== null
}

function deriveScoreStatus(
  state: AssessmentGradingState,
  submission: GradingSubmission
): ScoreStatus {
  const activeQuestions = state.questions.filter((q) => !q.thrownOut)
  const hasAnyPoints = activeQuestions.some((q) => {
    const r = submission.responses.find((rr) => rr.questionId === q.id)
    return responseHasScore(q, r)
  })

  if (!hasAnyPoints) return "Ungraded"

  const allScored = activeQuestions.every((q) => {
    const r = submission.responses.find((rr) => rr.questionId === q.id)
    return responseHasScore(q, r)
  })

  if (
    submission.scoreStatus === "Scores Released" &&
    allScored &&
    submission.scoresReleasedAt
  ) {
    return "Scores Released"
  }

  return allScored ? "Graded" : "Partially Evaluated"
}

function recomputeStatuses(state: AssessmentGradingState) {
  state.submissions.forEach((s) => {
    s.scoreStatus = deriveScoreStatus(state, s)
  })
}


/** Clears manual slots only; objective items stay auto-graded. */
function clearSubjectiveScoring(
  state: AssessmentGradingState,
  submission: GradingSubmission,
) {
  for (const q of state.questions) {
    if (q.thrownOut || !isSubjectiveType(q.type)) continue
    const response = submission.responses.find((r) => r.questionId === q.id)
    if (!response) continue
    response.pointsAwarded = null
    response.overridden = false
  }
  autoGradeObjectiveResponses(state.questions, submission)
  submission.gradeStatus = "Not Set"
  submission.grade = ""
  submission.scoresReleasedAt = undefined
  submission.lastReleasedTotal = undefined
  submission.gradesReleasedAt = undefined
  submission.scoreStatus = deriveScoreStatus(state, submission)
}

function offeringSlugFromAssessmentId(assessmentId: string): string | null {
  const match = assessmentId.match(/-a(\d+)$/)
  return match ? `a${match[1]}` : null
}

/** Lab Practical: submissions exist but faculty has not opened the scoring queue. */
function applyEvaluatingFreshProfile(state: AssessmentGradingState) {
  state.scoringStarted = false
  for (const submission of state.submissions) {
    if (submission.thrownOut) continue
    clearSubjectiveScoring(state, submission)
  }
}

/** Midterm: two rows stay unscored until faculty opens them. */
function applyMidtermNotStartedSamples(state: AssessmentGradingState) {
  const targets = state.submissions.filter((s) => !s.thrownOut).slice(-2)
  for (const submission of targets) {
    clearSubjectiveScoring(state, submission)
  }
}

export function isEvaluatingScoringNotStarted(
  state: AssessmentGradingState,
): boolean {
  if (state.scoringStarted === false) return true
  return false
}

export function markScoringStarted(assessmentId: string) {
  const state = getGradingState(assessmentId)
  if (state.scoringStarted !== false) return
  state.scoringStarted = true
  recomputeStatuses(state)
  notify(assessmentId)
}

function ensureSubmissionFullyScored(
  questions: GradingQuestion[],
  submission: GradingSubmission,
  rand: () => number,
) {
  for (const q of questions) {
    if (q.thrownOut) continue
    const response = submission.responses.find((r) => r.questionId === q.id)
    if (!response || response.pointsAwarded != null) continue
    if (isSubjectiveType(q.type)) {
      const pts = effectivePoints(q)
      response.pointsAwarded =
        Math.round(pts * (0.55 + rand() * 0.4) * 2) / 2
      if (!response.textResponse) {
        response.textResponse =
          SUBJECTIVE_SAMPLE_RESPONSES[q.id] ??
          "Student response submitted for this question."
      }
    } else {
      response.pointsAwarded = scoreResponse(q, response)
    }
  }
}

/** Results Published: every active submission is fully scored (released or graded only). */
function applyResultsPublishedProfile(state: AssessmentGradingState) {
  const rand = seededRandom(`${state.assessmentId}-published`)
  let releaseSlot = 0
  for (const submission of state.submissions) {
    if (submission.thrownOut) continue
    ensureSubmissionFullyScored(state.questions, submission, rand)
    const totals = computeTotals(state.questions, submission.responses)
    const preferReleased = releaseSlot < 3 || rand() < 0.55
    if (preferReleased) {
      submission.scoreStatus = "Scores Released"
      submission.scoresReleasedAt =
        submission.scoresReleasedAt ?? "2026-07-25 10:00"
      submission.lastReleasedTotal = totals.earned
    } else {
      submission.scoreStatus = "Graded"
    }
    releaseSlot += 1
  }
}

export type GradingSubmissionsDemoState = "all_data" | "no_data"

/* ── State construction ──────────────────────────────────────────────────── */
function buildInitialState(assessmentId: string): AssessmentGradingState {
  const questions = buildQuestions()
  const lifecycle = resolveAssessmentLifecycleStatus(assessmentId)
  const isPreSubmission =
    lifecycle === "Draft" ||
    lifecycle === "Scheduled" ||
    lifecycle === "Live"
  const submissions = isPreSubmission
    ? []
    : buildSubmissions(assessmentId, questions)
  const rand = seededRandom(`${assessmentId}-order`)
  const displayOrder = isPreSubmission
    ? []
    : shuffle(
        submissions.map((s) => s.id),
        rand,
      )
  const state: AssessmentGradingState = {
    assessmentId,
    blindGrading: seededRandom(assessmentId)() > 0.5,
    questions,
    submissions,
    displayOrder,
    nextQueue: [],
    regradeInProgress: false,
    regradeProgress: null,
    lastRegradeSummary: null,
    scoringStarted: true,
  }
  recomputeStatuses(state)
  const offeringSlug = offeringSlugFromAssessmentId(assessmentId)
  if (offeringSlug === "a3") {
    applyEvaluatingFreshProfile(state)
  } else if (offeringSlug === "a1") {
    applyMidtermNotStartedSamples(state)
  }
  if (resolveAssessmentLifecycleStatus(assessmentId) === "Results Published") {
    applyResultsPublishedProfile(state)
  }
  return state
}

export function resetGradingDemoState(
  assessmentId: string,
  mode: GradingSubmissionsDemoState,
) {
  if (mode === "no_data") {
    const base = buildInitialState(assessmentId)
    STORE[assessmentId] = {
      ...base,
      submissions: [],
      displayOrder: [],
      nextQueue: [],
    }
  } else {
    STORE[assessmentId] = buildInitialState(assessmentId)
  }
  notify(assessmentId)
}

function ensureShuffledDisplayOrder(state: AssessmentGradingState): void {
  const planOrder = state.submissions.map((s) => s.id)
  if (planOrder.length === 0) {
    state.displayOrder = []
    return
  }
  const matchesPlanOrder =
    state.displayOrder.length === planOrder.length &&
    state.displayOrder.every((id, i) => id === planOrder[i])
  if (!state.displayOrder.length || matchesPlanOrder) {
    const rand = seededRandom(`${state.assessmentId}-order`)
    state.displayOrder = shuffle(planOrder, rand)
  }
}

export function getGradingState(assessmentId: string): AssessmentGradingState {
  if (!STORE[assessmentId])
    STORE[assessmentId] = buildInitialState(assessmentId)
  ensureShuffledDisplayOrder(STORE[assessmentId])
  return STORE[assessmentId]
}

/** Active questions in the order this student saw them on delivery. */
export function questionsForSubmission(
  state: AssessmentGradingState,
  submission: GradingSubmission,
): GradingQuestion[] {
  const active = state.questions.filter((q) => !q.thrownOut)
  const byId = new Map(active.map((q) => [q.id, q]))
  const ids =
    submission.questionOrder.length > 0
      ? submission.questionOrder
      : active.map((q) => q.id)
  return ids
    .map((id) => byId.get(id))
    .filter((q): q is GradingQuestion => q != null)
}

/* ── Actions ──────────────────────────────────────────────────────────────── */
export function throwOutSubmissions(
  assessmentId: string,
  submissionIds: string[],
) {
  const state = getGradingState(assessmentId)
  const now = "just now"
  state.submissions.forEach((s) => {
    if (!submissionIds.includes(s.id)) return
    s.thrownOut = true
    s.lastNotifiedAt = now
  })
  notify(assessmentId)
}

export function restoreSubmissions(
  assessmentId: string,
  submissionIds: string[]
) {
  const state = getGradingState(assessmentId)
  state.submissions.forEach((s) => {
    if (!submissionIds.includes(s.id)) return
    s.thrownOut = false
    if (s.scoreStatus === "Scores Released") s.lastNotifiedAt = "just now"
  })
  notify(assessmentId)
}

export function throwOutQuestions(
  assessmentId: string,
  questionIds: string[],
  comment: string
) {
  const state = getGradingState(assessmentId)
  state.questions.forEach((q) => {
    if (!questionIds.includes(q.id)) return
    q.thrownOut = true
    q.throwOutComment = comment
  })
  recomputeStatuses(state)
  notify(assessmentId)
}

export function restoreQuestions(assessmentId: string, questionIds: string[]) {
  const state = getGradingState(assessmentId)
  state.questions.forEach((q) => {
    if (!questionIds.includes(q.id)) return
    q.thrownOut = false
    q.throwOutComment = undefined
  })
  recomputeStatuses(state)
  notify(assessmentId)
}

export interface KeyAdjustmentDraft {
  questionId: string
  addOptionCorrect?: string[]
  /** Applied after `addOptionCorrect`, so a removal always wins. */
  removeOptionCorrect?: string[]
  addFitbKeywords?: { blankIndex: number; keyword: string }[]
  removeFitbKeywords?: { blankIndex: number; keyword: string }[]
  addNumericRange?: { min: number; max: number }
  setCorrectOption?: string
  setMatchingPair?: Record<string, string>
  setPoints?: number
  setBonusForAll?: boolean
  setScoringMode?: ScoringMode
}

export function scoreSubjectiveResponse(
  assessmentId: string,
  submissionId: string,
  questionId: string,
  points: number | null
) {
  const state = getGradingState(assessmentId)
  const submission = state.submissions.find((s) => s.id === submissionId)
  if (!submission || submission.thrownOut) return
  const question = state.questions.find((q) => q.id === questionId)
  if (!question) return
  const response = submission.responses.find((r) => r.questionId === questionId)
  if (!response) return
  if (points === null) {
    response.pointsAwarded = null
  } else {
    const clamped = Math.max(0, Math.min(effectivePoints(question), points))
    response.pointsAwarded = clamped
  }
  submission.scoreStatus = deriveScoreStatus(state, submission)
  notify(assessmentId)
}

export function setGrade(
  assessmentId: string,
  submissionId: string,
  grade: string
) {
  const state = getGradingState(assessmentId)
  const submission = state.submissions.find((s) => s.id === submissionId)
  if (!submission || submission.thrownOut) return
  submission.grade = grade
  submission.gradeStatus = grade.trim()
    ? submission.gradeStatus === "Grades Released"
      ? "Grades Released"
      : "Set"
    : "Not Set"
  notify(assessmentId)
}

export function applyChallengeOverride(
  assessmentId: string,
  submissionId: string,
  questionId: string,
  newPoints: number
) {
  const state = getGradingState(assessmentId)
  const submission = state.submissions.find((s) => s.id === submissionId)
  if (!submission || submission.thrownOut || submission.scoreStatus !== "Scores Released") return
  const question = state.questions.find((q) => q.id === questionId)
  if (!question) return
  const clamped = Math.max(0, Math.min(effectivePoints(question), newPoints))
  const response = submission.responses.find((r) => r.questionId === questionId)
  if (!response) return
  response.pointsAwarded = clamped
  response.overridden = true
  const totals = submissionTotals(state, submission)
  if (totals.earned !== submission.lastReleasedTotal) {
    submission.lastNotifiedAt = "just now"
    submission.lastReleasedTotal = totals.earned
  }
  notify(assessmentId)
}

export function saveKeyAdjustments(
  assessmentId: string,
  drafts: KeyAdjustmentDraft[],
  onDone?: (summary: { notifiedCount: number }) => void
) {
  const state = getGradingState(assessmentId)
  drafts.forEach((d) => {
    const q = state.questions.find((qq) => qq.id === d.questionId)
    if (!q) return
    if (d.setCorrectOption) q.correctOptionKeys = [d.setCorrectOption]
    if (d.addOptionCorrect?.length)
      q.correctOptionKeys = [
        ...new Set([...(q.correctOptionKeys || []), ...d.addOptionCorrect]),
      ]
    if (d.removeOptionCorrect?.length)
      q.correctOptionKeys = (q.correctOptionKeys || []).filter(
        (k) => !d.removeOptionCorrect!.includes(k)
      )
    if (d.removeFitbKeywords?.length && q.blanks) {
      d.removeFitbKeywords.forEach(({ blankIndex, keyword }) => {
        const blank = q.blanks![blankIndex]
        if (blank)
          blank.acceptableKeywords = blank.acceptableKeywords.filter(
            (k) => k !== keyword
          )
      })
    }
    if (d.addFitbKeywords?.length && q.blanks) {
      d.addFitbKeywords.forEach(({ blankIndex, keyword }) => {
        const blank = q.blanks![blankIndex]
        if (blank)
          blank.acceptableKeywords = [
            ...new Set([...blank.acceptableKeywords, keyword]),
          ]
      })
    }
    if (d.addNumericRange)
      q.acceptableRanges = [...(q.acceptableRanges || []), d.addNumericRange]
    if (d.setMatchingPair && q.matchingPairs) {
      Object.entries(d.setMatchingPair).forEach(([pairId, right]) => {
        const pair = q.matchingPairs!.find((p) => p.id === pairId)
        if (pair) pair.right = right
      })
    }
    if (d.setPoints != null) q.adjustedPoints = d.setPoints
    if (d.setBonusForAll != null) q.bonusForAll = d.setBonusForAll
    if (d.setScoringMode) q.scoringMode = d.setScoringMode
  })

  state.regradeInProgress = true
  state.regradeProgress = { done: 0, total: state.submissions.length }
  notify(assessmentId)

  let done = 0
  const step = () => {
    done += 1
    state.regradeProgress = { done, total: state.submissions.length }
    if (done <= state.submissions.length) {
      const submission = state.submissions[done - 1]
      submission.responses = submission.responses.map((r) => {
        const q = state.questions.find((qq) => qq.id === r.questionId)
        if (!q || isSubjectiveType(q.type)) return r
        return { ...r, pointsAwarded: scoreResponse(q, r) }
      })
      recomputeStatuses(state)
    }
    notify(assessmentId)
    if (done < state.submissions.length) {
      setTimeout(step, 120)
    } else {
      let notifiedCount = 0
      state.submissions.forEach((s) => {
        if (s.scoreStatus !== "Scores Released") return
        const totals = submissionTotals(state, s)
        if (totals.earned !== s.lastReleasedTotal) {
          s.lastNotifiedAt = "just now"
          s.lastReleasedTotal = totals.earned
          notifiedCount += 1
        }
      })
      state.regradeInProgress = false
      state.regradeProgress = null
      state.lastRegradeSummary = { notifiedCount }
      notify(assessmentId)
      onDone?.({ notifiedCount })
    }
  }
  setTimeout(step, 250)
}

export function releaseScores(assessmentId: string) {
  const state = getGradingState(assessmentId)
  const toRelease = state.submissions.filter(
    (s) => !s.thrownOut && s.scoreStatus === "Graded"
  )
  toRelease.forEach((s) => {
    s.scoreStatus = "Scores Released"
    s.scoresReleasedAt = "just now"
    s.lastReleasedTotal = submissionTotals(state, s).earned
  })
  notify(assessmentId)
  return { releasedIds: toRelease.map((s) => s.id) }
}

export function releaseGrades(assessmentId: string) {
  const state = getGradingState(assessmentId)
  const toRelease = state.submissions.filter(
    (s) =>
      !s.thrownOut &&
      s.scoreStatus === "Scores Released" &&
      s.gradeStatus === "Set"
  )
  toRelease.forEach((s) => {
    s.gradeStatus = "Grades Released"
    s.gradesReleasedAt = "just now"
  })
  notify(assessmentId)
  return { releasedIds: toRelease.map((s) => s.id) }
}

export function hasNextUngradedSubmission(
  assessmentId: string,
  currentId?: string
): boolean {
  const state = getGradingState(assessmentId)
  return state.submissions.some(
    (s) => !s.thrownOut && needsScoringStatus(s.scoreStatus) && s.id !== currentId
  )
}

export function getNextSubmissionId(
  assessmentId: string,
  currentId?: string
): string | null {
  const state = getGradingState(assessmentId)
  const pool = state.submissions
    .filter(
      (s) => !s.thrownOut && needsScoringStatus(s.scoreStatus) && s.id !== currentId
    )
    .map((s) => s.id)
  if (pool.length === 0) return null
  state.nextQueue = state.nextQueue.filter((id) => pool.includes(id))
  if (state.nextQueue.length === 0) {
    state.nextQueue = shuffle(
      pool,
      seededRandom(`${assessmentId}-next-${Math.random()}`)
    )
  }
  const nextId = state.nextQueue.shift() ?? null
  notify(assessmentId)
  return nextId
}

/* ── React binding ────────────────────────────────────────────────────────── */
export function useGradingState(assessmentId: string): AssessmentGradingState {
  const state = getGradingState(assessmentId)
  const [, forceRender] = React.useReducer((c) => c + 1, 0)
  React.useEffect(() => {
    return subscribeGradingState(assessmentId, () => {
      forceRender()
    })
  }, [assessmentId])
  return state
}

/** Manual scoring KPI — one slot per manual question on each active submission. */
export function manualScoringProgress(state: AssessmentGradingState) {
  const active = state.submissions.filter((s) => !s.thrownOut)
  const subjectiveQuestions = state.questions.filter(
    (q) => !q.thrownOut && isSubjectiveType(q.type),
  )

  let manualSlotsTotal = 0
  let manualSlotsScored = 0

  for (const question of subjectiveQuestions) {
    for (const submission of active) {
      manualSlotsTotal += 1
      const response = submission.responses.find(
        (r) => r.questionId === question.id,
      )
      if (response != null && response.pointsAwarded !== null) {
        manualSlotsScored += 1
      }
    }
  }

  const manualCompletePct =
    manualSlotsTotal === 0
      ? 0
      : manualSlotsScored === manualSlotsTotal
        ? 100
        : Math.min(99, Math.floor((manualSlotsScored / manualSlotsTotal) * 100))

  return { manualSlotsTotal, manualSlotsScored, manualCompletePct }
}

/**
 * Evaluating / Results Published header KPIs — grading state wins over mock
 * submission rows (e.g. Lab Practical before Start scoring).
 */
export function gradingHeaderKpiFigures(state: AssessmentGradingState) {
  const active = state.submissions.filter((s) => !s.thrownOut)
  const notStarted = isEvaluatingScoringNotStarted(state)
  const gradedCount = notStarted
    ? 0
    : active.filter((s) => isFullyScored(s.scoreStatus)).length

  const scoreFigures = notStarted
    ? {
        avgScore: null as number | null,
        minScore: null as number | null,
        maxScore: null as number | null,
        scorePossible:
          active.length > 0
            ? submissionTotals(state, active[0]).possible
            : state.questions
                .filter((q) => !q.thrownOut)
                .reduce((sum, q) => sum + effectivePoints(q), 0),
      }
    : submissionScoreFigures(state)

  return {
    submittedCount: active.length,
    gradedCount,
    pendingManualCount: active.filter((s) =>
      needsScoringStatus(s.scoreStatus),
    ).length,
    ...manualScoringProgress(state),
    ...scoreFigures,
  }
}

/** Earned points across fully scored submissions (not percent). */
export function submissionScoreFigures(state: AssessmentGradingState) {
  const active = state.submissions.filter((s) => !s.thrownOut)
  const fullyScoredSubs = active.filter((s) => isFullyScored(s.scoreStatus))
  const scores = fullyScoredSubs
    .map((s) => submissionTotals(state, s).earned)
    .sort((a, b) => a - b)
  const scorePossible =
    active.length > 0
      ? submissionTotals(state, active[0]).possible
      : state.questions
          .filter((q) => !q.thrownOut)
          .reduce((sum, q) => sum + effectivePoints(q), 0)
  const avgScore = scores.length
    ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
    : null
  const minScore = scores.length ? scores[0] : null
  const maxScore = scores.length ? scores[scores.length - 1] : null

  return { avgScore, minScore, maxScore, scorePossible }
}

/* ── Analytics ────────────────────────────────────────────────────────────── */
export function gradingKpiMetrics(state: AssessmentGradingState) {
  const active = state.submissions.filter((s) => !s.thrownOut)
  const needsScoring = active.filter((s) => needsScoringStatus(s.scoreStatus)).length
  const fullyScoredSubs = active.filter((s) => isFullyScored(s.scoreStatus))
  const { avgScore: avg, minScore: min, maxScore: max, scorePossible: possible } =
    submissionScoreFigures(state)

  // Manual (subjective) questions auto-grade nowhere. Progress counts every
  // manual question on every active submission (e.g. 2 questions × 8 subs = 16).
  const { manualSlotsTotal, manualSlotsScored, manualCompletePct } =
    manualScoringProgress(state)
  const manualQuestionCount = state.questions.filter(
    (q) => !q.thrownOut && isSubjectiveType(q.type),
  ).length

  // The scored-submission total is the strip's anchor, so it is stated once on
  // the leading tile; the score tiles only qualify the sample they summarize.
  const sampleCaption = `across ${fullyScoredSubs.length} scored ${
    fullyScoredSubs.length === 1 ? "submission" : "submissions"
  }`

  return [
    {
      id: "pending",
      label: SCORING_KPI_LABEL.pending,
      // Headline carries only the live count so it reads at a glance; the
      // caption supplies the total and the completed share the bar plots.
      value: active.length ? needsScoring : "None",
      trend: "neutral" as const,
      delta: "",
      description: active.length
        ? `${fullyScoredSubs.length} of ${active.length} scored`
        : "no submissions yet",
      ...(active.length > 0
        ? {
            progress: fullyScoredSubs.length,
            progressMax: active.length,
            progressTone: "brand" as const,
          }
        : {}),
    },
    {
      id: "score-range",
      label: SCORING_KPI_LABEL.range,
      value: fullyScoredSubs.length && min != null && max != null
        ? `${min} to ${max}`
        : "None",
      trend: "neutral" as const,
      delta: "",
      description:
        fullyScoredSubs.length && avg != null
          ? `Average ${avg}, ${sampleCaption}`
          : "none scored yet",
      // Needs two distinct scores before a span means anything; one scored
      // submission is a point, not a range.
      ...(fullyScoredSubs.length > 1 &&
      possible != null &&
      possible > 0 &&
      min != null &&
      max != null &&
      avg != null &&
      max > min
        ? { rangeTrack: { min, avg, max, possible } }
        : {}),
    },
    {
      id: "scoring-progress",
      label: SCORING_KPI_LABEL.manualScoring,
      value: manualSlotsTotal ? `${manualCompletePct}%` : "None",
      trend: "neutral" as const,
      delta: "",
      description: !active.length
        ? "no submissions yet"
        : manualQuestionCount === 0
          ? "no manual questions to score"
          : manualSlotsScored === manualSlotsTotal
            ? `all ${manualSlotsTotal} questions scored`
            : `${manualSlotsScored} of ${manualSlotsTotal} questions`,
      // An assessment with no manual questions has nothing to plot; an empty
      // rail there reads as work not started rather than work not required.
      ...(manualSlotsTotal > 0
        ? {
            progress: manualSlotsScored,
            progressMax: manualSlotsTotal,
            progressTone: "brand" as const,
          }
        : {}),
    },
  ]
}

/** Letter-grade KPI tiles for the submissions hub strip. */
export function gradingGradeKpiMetrics(state: AssessmentGradingState) {
  const active = state.submissions.filter((s) => !s.thrownOut)
  const noGrade = active.filter(
    (s) => s.gradeStatus === "Not Set" && !isEvaluationNotStarted(s.scoreStatus)
  ).length
  const readyToRelease = active.filter(
    (s) => s.scoreStatus === "Scores Released" && s.gradeStatus === "Set"
  ).length
  const released = active.filter(
    (s) => s.gradeStatus === "Grades Released"
  ).length
  const gradesSet = active.filter(
    (s) => s.gradeStatus === "Set" || s.gradeStatus === "Grades Released"
  ).length
  const hasGrades = gradesSet > 0

  return [
    {
      id: "no-grade",
      label: GRADING_KPI_LABEL.noGrade,
      value: noGrade,
      trend: "neutral" as const,
      delta: "",
      description: gradingKpiDescription("noGrade", {
        activeCount: active.length,
        readyCount: readyToRelease,
        noGradeCount: noGrade,
        hasGrades,
      }),
    },
    {
      id: "ready",
      label: GRADING_KPI_LABEL.ready,
      value: readyToRelease,
      trend: "neutral" as const,
      delta: "",
      description: gradingKpiDescription("ready", {
        activeCount: active.length,
        readyCount: readyToRelease,
        noGradeCount: noGrade,
        hasGrades,
      }),
    },
    {
      id: "released",
      label: GRADING_KPI_LABEL.released,
      value: released,
      trend: "neutral" as const,
      delta: "",
      description: gradingKpiDescription("released", {
        activeCount: active.length,
        readyCount: readyToRelease,
        noGradeCount: noGrade,
        hasGrades,
      }),
    },
    {
      id: "grades-set",
      label: GRADING_KPI_LABEL.set,
      value: gradesSet,
      trend: "neutral" as const,
      delta: "",
      description: gradingKpiDescription("set", {
        activeCount: active.length,
        readyCount: readyToRelease,
        noGradeCount: noGrade,
        hasGrades,
      }),
    },
  ]
}

export function dotPlotData(
  state: AssessmentGradingState,
  submissions: GradingSubmission[]
) {
  const sorted = [...submissions].sort((a, b) =>
    a.submittedAt.localeCompare(b.submittedAt)
  )
  const points = sorted.map((s, i) => ({
    x: i + 1,
    y: submissionTotals(state, s).earned,
    label: s.maskLabel,
  }))
  if (points.length === 0)
    return { points, trend: [] as { x: number; y: number }[] }
  const n = points.length
  const sumX = points.reduce((a, p) => a + p.x, 0)
  const sumY = points.reduce((a, p) => a + p.y, 0)
  const sumXY = points.reduce((a, p) => a + p.x * p.y, 0)
  const sumXX = points.reduce((a, p) => a + p.x * p.x, 0)
  const denom = n * sumXX - sumX * sumX
  const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0
  const intercept = (sumY - slope * sumX) / n
  const trend = [
    { x: points[0].x, y: intercept + slope * points[0].x },
    { x: points[n - 1].x, y: intercept + slope * points[n - 1].x },
  ]
  return { points, trend }
}
